import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { getMe, MfaInfo } from '../api';
import { useAuth } from './AuthProvider';
import MfaEnroll from './MfaEnroll';

function daysLeft(deadline: string): number {
  return Math.ceil((new Date(deadline).getTime() - Date.now()) / 86_400_000);
}
function fmt(deadline: string): string {
  return new Date(deadline).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

const MfaGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [mfa, setMfa] = useState<MfaInfo | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('mfaBannerDismissed') === '1');

  const refresh = useCallback(() => {
    if (!user) { setLoaded(true); return; }
    getMe().then(me => { setMfa(me.mfa); setLoaded(true); }).catch(() => setLoaded(true));
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  // Not signed in, still loading, federated, or already enrolled → no gating.
  if (!user) return <>{children}</>;            // unauthenticated — ProtectedRoute handles it
  if (!loaded) return null;                      // awaiting /api/me — render nothing, no flash
  if (!mfa || mfa.federated || mfa.enabled) return <>{children}</>;

  if (mfa.graceExpired) {
    return (
      <div style={s.blockWrap}>
        <div style={s.blockMsg}>
          <h1 style={s.blockH}>Two-factor authentication required</h1>
          <p style={s.blockP}>Access to the portal now requires MFA. Set it up below to continue.</p>
        </div>
        <MfaEnroll email={user.email} onEnrolled={refresh} />
      </div>
    );
  }

  const dl = mfa.deadline ? `${fmt(mfa.deadline)} — ${daysLeft(mfa.deadline)} day(s) left` : 'soon';
  return (
    <>
      {!dismissed && (
        <div style={s.banner}>
          <span>Set up two-factor authentication by <strong>{dl}</strong>.</span>
          <span style={s.actions}>
            <Link to="/security" style={s.btn}>Set up now</Link>
            <button style={s.x} onClick={() => { sessionStorage.setItem('mfaBannerDismissed', '1'); setDismissed(true); }}>×</button>
          </span>
        </div>
      )}
      {children}
    </>
  );
};

const s: Record<string, React.CSSProperties> = {
  banner: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', background: '#fff3cd', color: '#856404', border: '1px solid #ffe69c', borderRadius: 6, padding: '0.6rem 1rem', margin: '0.75rem 1rem', fontSize: '0.88rem' },
  actions: { display: 'flex', alignItems: 'center', gap: '0.75rem', whiteSpace: 'nowrap' as const },
  btn: { background: '#856404', color: '#fff', borderRadius: 4, padding: '0.25rem 0.7rem', fontSize: '0.8rem', fontWeight: 600, textDecoration: 'none' },
  x: { background: 'none', border: 'none', color: '#856404', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1 },
  blockWrap: { maxWidth: 520, margin: '3rem auto', padding: '0 1rem' },
  blockMsg: { textAlign: 'center' as const, marginBottom: '0.5rem' },
  blockH: { fontSize: '1.4rem', fontWeight: 700, marginBottom: '0.5rem' },
  blockP: { color: '#666', fontSize: '0.9rem' },
};

export default MfaGate;
